import { useEffect, useState } from "react";
import { X, Loader2, Banknote, BadgeCheck, Mail } from "lucide-react";
import { api, apiClient } from "@/lib/api";
import { useToast } from "@/lib/toast";
import { formatCurrency } from "@/lib/format";
import { Field } from "@/components/UI";

const STATUSES = [
  { value: "unpaid", label: "Unpaid", cls: "bg-red-50 text-red-700 border-red-200" },
  { value: "partial", label: "Partially Paid", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  { value: "paid", label: "Paid", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
];

export const paymentBadge = (d) => {
  const s = d.payment_status || "unpaid";
  const map = {
    unpaid: { label: "Unpaid", cls: "bg-red-100 text-red-700" },
    partial: { label: "Partial", cls: "bg-amber-100 text-amber-700" },
    paid: { label: "Paid", cls: "bg-emerald-100 text-emerald-700" },
  };
  return map[s] || map.unpaid;
};

export const PaymentModal = ({ modal, setModal, onSaved }) => {
  const { toast } = useToast();
  const [status, setStatus] = useState("unpaid");
  const [amount, setAmount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const d = modal.doc;

  useEffect(() => {
    if (modal.open && modal.doc) {
      setStatus(modal.doc.payment_status || "unpaid");
      setAmount(modal.doc.amount_paid || 0);
    }
  }, [modal.open, modal.doc]);

  if (!modal.open || !d) return null;

  const sub = (d.items || []).reduce((a, i) => a + (i.qty || 0) * (i.unit_price || 0), 0);
  const total = sub + (sub * (d.tax_rate || 0)) / 100;
  const received = status === "paid" ? total : (parseFloat(amount) || 0);
  const outstanding = Math.max(total - received, 0);
  const close = () => setModal({ open: false, doc: null });

  const save = async () => {
    setSaving(true);
    try {
      await api.updateDmsDoc(d.id, {
        payment_status: status,
        amount_paid: status === "paid" ? total : status === "unpaid" ? 0 : (parseFloat(amount) || 0),
      });
      toast(`Payment updated — ${d.serial}`, "success");
      close();
      onSaved?.();
    } catch (e) {
      toast(e?.response?.data?.detail || "Update failed", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="payment-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-5 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-bold text-base flex items-center gap-2"><Banknote size={16} className="text-[#0e6939]" /> Payment</h3>
            <p className="text-xs muted mt-0.5">{d.serial} · Total {formatCurrency(total, d.currency)}</p>
          </div>
          <button onClick={close} className="text-slate-400 hover:text-slate-600" data-testid="payment-close">
            <X size={16} />
          </button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {STATUSES.map((s) => (
            <button
              key={s.value}
              data-testid={`payment-status-${s.value}`}
              onClick={() => setStatus(s.value)}
              className={`text-xs font-semibold px-2 py-2 rounded-lg border ${s.cls} ${status === s.value ? "ring-2 ring-[#0e6939]" : "opacity-60 hover:opacity-100"}`}
            >
              {s.label}
            </button>
          ))}
        </div>
        {status === "partial" && (
          <Field label={`Amount received (${d.currency || "UGX"})`}>
            <input
              data-testid="payment-amount"
              type="number"
              className="input"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              min={0}
            />
          </Field>
        )}
        <div className="text-sm flex justify-between border-t border-slate-100 pt-3">
          <span className="muted">Outstanding balance</span>
          <span className={`font-bold ${outstanding > 0 ? "text-red-600" : "text-emerald-700"}`} data-testid="payment-outstanding">
            {formatCurrency(outstanding, d.currency)}
          </span>
        </div>
        <div className="flex justify-end gap-2">
          {d.payment_status === "paid" && (
            <div className="mr-auto flex items-center gap-1">
              <button
                data-testid="payment-receipt"
                className="btn btn-ghost text-emerald-700"
                onClick={async () => {
                  try {
                    const res = await apiClient.get(`/dms/documents/${d.id}/receipt`, { responseType: "blob" });
                    const url = URL.createObjectURL(res.data);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `RCPT_${(d.serial || "receipt").replace(/\//g, "_")}.pdf`;
                    a.click();
                    URL.revokeObjectURL(url);
                  } catch {
                    toast("Receipt download failed", "error");
                  }
                }}
              >
                <BadgeCheck size={14} /> Receipt
              </button>
              <button
                data-testid="payment-email-receipt"
                className="btn btn-ghost text-emerald-700"
                disabled={emailing}
                title={d.client_email ? `Email receipt to ${d.client_email}` : "Add the client's email first"}
                onClick={async () => {
                  if (!d.client_email) { toast("Add the client's email on the quotation first", "error"); return; }
                  setEmailing(true);
                  try {
                    const res = await api.emailReceipt(d.id);
                    toast(`Receipt emailed to ${res.to}`, "success");
                  } catch (e) {
                    toast(e?.response?.data?.detail || "Email failed", "error");
                  } finally {
                    setEmailing(false);
                  }
                }}
              >
                {emailing ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Email receipt
              </button>
            </div>
          )}
          <button className="btn btn-ghost" onClick={close}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving} data-testid="payment-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : null} Save
          </button>
        </div>
      </div>
    </div>
  );
};
